/**
 * Client-side SDMS mail-merge — the port of legacy ALPACA's DevExpress
 * `GenerateMailMerge` (BTLIMS SpreadsheetEntryController.cs:3398).
 *
 * Runs in the browser with **ExcelJS** (the same engine Univer uses to read these
 * DevExpress-authored templates) so the merged workbook stays ExcelJS-readable — a
 * server-side ClosedXML merge emits namespace-prefixed OOXML that ExcelJS's strict
 * parser rejects (verified 2026-07-28). The backend supplies the raw template + the
 * expanded records (`AI_Analysis_Sdms_MergeData_SP`); this does the substitution.
 *
 * The template stores merge fields as `FIELD("COLUMN")` formulas. Rows above the
 * detail band carry single header FIELDs (TEST/METHOD/…); the band row (the one with
 * the most FIELD cells — the template's `DETAILRANGE`) is repeated once per record.
 * EVERY worksheet is merged, each against the dataset OG binds to its NAME (see the
 * datasetFor table in mergeSdmsTemplate): the main sheet and any unnamed extra sheet →
 * the RawDataTable records; "Sequence" → the distinct per-run rows; "Summary"/"ISTD" →
 * the records; "Reagents"/"Instruments" → their own datasets, Traceability-gated; the
 * Calibration / "…-Oriented Summary" sheets are excluded as in OG. Result columns (Reading / NumericResult / Qualifier /
 * QCStatus) stay blank — the analyst enters them.
 */
import { writeSdmsWorkbook } from './sdmsWorkbookIo';
import { resolveMissingSheetRefs } from './sdmsMissingSheetRefs';
import { applyOgSheetOrder } from './sdmsSheetOrder';

type Row = Record<string, unknown>;

const FIELD_RE = /FIELD\("([^"]*)"\)/gi;
const PURE_RE  = /^\s*FIELD\("([^"]*)"\)\s*$/i;
// Some template cells carry merge fields as a TEXT placeholder `[[COLUMN]]`
// instead of a `FIELD("COLUMN")` formula (e.g. the CtrlLimit columns). These live
// in plain-string cells, so the FIELD/formula path above never sees them — they
// must be substituted separately or they render literally (`[[%RPDULIMIT]]`) and a
// dependent formula errors `#VALUE!`. (fix 2026-07-30)
const BRACKET_RE      = /\[\[([^\]]+)\]\]/g;          // global — for replace
const BRACKET_PURE_RE = /^\s*\[\[([^\]]+)\]\]\s*$/;   // whole cell is one [[FIELD]]
const HAS_FIELD       = /FIELD\(/i;                   // non-global — safe for .test()
const HAS_BRACKET     = /\[\[[^\]]+\]\]/;

/** FIELD names that are LEGITIMATELY absent from the merge dataset — the analyst
 *  (or an instrument import) supplies them AFTER the merge, so the template cell
 *  must start blank. Everything else that a template references but the dataset
 *  does not carry is template↔SP drift — a data-accuracy bug in the making (the
 *  ISTD Acceptance Range shipped blank for exactly this reason) — and gets a
 *  console warning below so it can never fail silently again. (2026-08-15) */
const ENTRY_FIELDS = new Set([
  'READING', 'READING1', 'READING2', 'NUMERICRESULT', 'QUALIFIER', 'QCSTATUS',
  'MASS', 'ABSORBANCE', 'INSTRUMENTRESULT', 'QCSAMPLERESULT', 'SHEETNAME',
  // Titration / gravimetric raw inputs (Alkalinity 8085 F7 = FIELD("VOLUMEUSED") is the
  // only typed cell there) — analyst-supplied after the merge, like Reading. (2026-09-22)
  'VOLUMEUSED', 'VT', 'VT1', 'TEMPERATURE', 'DLD',
  // Result-derived at entry time (OG's DataTable carries them EMPTY at create):
  'RPD', '%RECOVERY', 'DUPSOURCERESULT', 'RESPONSE',
  // Bound by 15 templates but supplied by NOTHING in OG either (verified: the
  // string exists nowhere in the OG source) — blank in OG too, so not drift:
  'ISREPORTDONE',
]);
/** USERDEFINED1..n / USERDEFINEDDATE1.. (pH temp/dilutions, PFAS responses/
 *  descriptions) are instrument-import/entry slots — legitimately absent at
 *  merge time. */
const ENTRY_FIELD_RE = /^USERDEFINED/;

/** Per-sheet right edge (1-based column) of the template's DETAILRANGE defined
 *  names, parsed from the raw xlsx zip (`xl/workbook.xml`). The sheet each
 *  range belongs to is taken from the range's own `Sheet!$A$1:$B$2` prefix, so
 *  sheet-local duplicates (Sheet1 + Reagents both define DETAILRANGE) resolve
 *  correctly where ExcelJS's definedNames model cannot. */
async function detailRangeEndCols(templateBuf: ArrayBuffer): Promise<Record<string, number>> {
  const out: Record<string, number> = {};
  const JSZip = (await import('jszip')).default;
  const zip = await JSZip.loadAsync(templateBuf);
  const xml = await zip.file('xl/workbook.xml')?.async('string');
  if (!xml) return out;
  for (const dn of xml.matchAll(/<definedName\b[^>]*>([^<]*)<\/definedName>/g)) {
    if (!/name="DETAILRANGE"/i.test(dn[0]!)) continue;
    const m = /^'?([^'!]+)'?!\$?[A-Za-z]{1,3}\$?\d+:\$?([A-Za-z]{1,3})\$?\d+$/.exec(dn[1]!.trim());
    if (m) out[m[1]!] = colToNum(m[2]!);
  }
  return out;
}
function colToNum(letters: string): number {
  let n = 0;
  for (const ch of letters.toUpperCase()) n = n * 26 + (ch.charCodeAt(0) - 64);
  return n;
}
function numToCol(n: number): string {
  let s = '';
  for (; n > 0; n = Math.floor((n - 1) / 26)) s = String.fromCharCode(65 + ((n - 1) % 26)) + s;
  return s;
}

/** OG's AutoFilter range on a merged sheet: the template filter's columns, from its header row down through the
 *  contiguous data block under it (the first row with nothing in those columns ends it) — the region Excel /
 *  DevExpress filter when a header-only range is applied. Null when the template range is unreadable. */
export function filterRefOver(
  ws: import('exceljs').Worksheet, src: NonNullable<import('exceljs').Worksheet['autoFilter']>,
): string | null {
  type Pt = string | { row: number; column: number };
  const at = (a: Pt | undefined): { col: number; row: number } | null => {
    if (a == null) return null;
    if (typeof a !== 'string') return { col: a.column, row: a.row };
    const m = /^\$?([A-Za-z]{1,3})\$?(\d+)$/.exec(a.trim());
    return m ? { col: colToNum(m[1]!), row: Number(m[2]) } : null;
  };
  let from: { col: number; row: number } | null, to: { col: number; row: number } | null;
  if (typeof src === 'string') { const [a, b] = src.split(':'); from = at(a); to = at(b ?? a); }
  else { const o = src as { from?: Pt; to?: Pt }; from = at(o.from); to = at(o.to); }
  if (!from || !to) return null;
  const c1 = Math.min(from.col, to.col), c2 = Math.max(from.col, to.col), r1 = Math.min(from.row, to.row);
  const hasValue = (r: number): boolean => {
    const row = ws.getRow(r);
    for (let c = c1; c <= c2; c++) {
      const v = row.getCell(c).value;
      if (v !== null && v !== undefined && v !== '') return true;
    }
    return false;
  };
  let last = Math.max(from.row, to.row);
  for (let r = r1 + 1; r <= ws.rowCount && hasValue(r); r++) last = Math.max(last, r);
  return `${numToCol(c1)}${r1}:${numToCol(c2)}${last}`;
}

/** Shift the RELATIVE row part of every A1 cell-reference in a formula by `k` — the
 *  standard Excel copy semantics ClosedXML would apply automatically. Absolute rows
 *  (`$8`) and refs that are part of a longer token (function names) are left alone. */
export function shiftFormula(f: string, k: number): string {
  if (!k) return f;
  // A quoted TEXT literal is never a reference: "CCV1.csv" / "LCS1" / "CCV1" (a run's sheet or
  // file name spliced in by the merge, or a QC label a template compares against) must not become
  // "CCV2.csv" on the next band row — which is exactly what happened to the VOC/THM SHEETNAME
  // column and the Sequence sheet (parity class 6, 2026-09-23). Split on double-quoted segments
  // and shift only the unquoted ones. A single-quoted SHEET NAME is not a reference either:
  // VOC 8086 BA8 `INDEX('CCV1'!J:J, …)` names the CCV1 run sheet, which became 'CCV61' on band
  // row 68 (2026-09-26) — the `'…'!` prefix is kept verbatim; the reference after `!` still shifts.
  return f.split(/("(?:[^"]|"")*"|'(?:[^']|'')+'!)/).map((part, i) => i % 2 === 1 ? part
    // `(?!\s*\()`: a name followed by "(" is a FUNCTION, not a cell — LOG10 / ATAN2 / DAYS360 are also
    // valid A1 shapes, and Colilert 8074 V `LOG10(…)` became `LOG24(…)` (#NAME?) on band row 22 (2026-09-26).
    : part.replace(/(\$?)\b([A-Z]{1,3})(\$?)(\d+)\b(?!\s*\()/g, (m, colDollar, col, rowDollar, row) =>
        rowDollar === '$' ? m : `${colDollar}${col}${rowDollar}${parseInt(row, 10) + k}`)).join('');
}

/** Excel-literal for a value embedded inside a formula — ALWAYS quoted TEXT.
 *
 *  DevExpress `FIELD()` returns TEXT, and the templates are authored for that:
 *  they compare fields to text (`FIELD("INTERNALSTANDARD")="1"`) and explicitly
 *  `VALUE(...)`-wrap a field before arithmetic. Splicing a NUMBER unquoted broke
 *  the text comparisons — the API sends InternalStandard as the NUMBER 1, so the
 *  ISTD Acceptance Range became `IF(1="1",…)` which is FALSE in Excel semantics
 *  (number ≠ text) and the column rendered blank while the sibling cells that
 *  reference the AU helper CELL (stored as text) worked. Quoting numbers is safe
 *  for arithmetic too: Univer coerces numeric text exactly like Excel (verified
 *  2026-08-13, see cellValue note). (fix 2026-08-14) */
function fieldLiteral(v: unknown, dateCell = false): string {
  if (v === undefined || v === null || v === '') return '""';
  // A date into a date-formatted cell is the Excel DATE SERIAL, unquoted — what DevExpress FIELD() puts in
  // a formula for a DateTime column (OG HAA BF8 `IF(D8="THAA5",BF7,46295.6201388889)`), so the cell's
  // "mm/dd/yyyy hh:mm" format shows it. As quoted text it showed the raw "2026-09-30 14:53:04-05:00"
  // (user report 2026-10-16).
  const d = dateCell ? canonicalDateText(v) : null;
  if (d) return dateSerialLiteral(d);
  return `"${String(v).replace(/"/g, '""')}"`;
}

/** A Date on the UTC axis (canonicalDateText) as the Excel serial literal OG writes (10 decimals). */
export function dateSerialLiteral(d: Date): string {
  return String(Number((d.getTime() / 86_400_000 + 25_569).toFixed(10)));
}

/** Re-splice NEW values for some FIELD tokens of an embedded-FIELD band formula, keeping every other
 *  token's value exactly as the merge spliced it.
 *
 *  OG Import Files puts the parsed values into dtsample and re-binds the sheet, so a template cell such
 *  as PFAS_533_Waters J8 `IF(ISTD,TEXT(G8,0),FIELD("USERDEFINED14"))` or HAA J8 (NUMERICRESULT) keeps
 *  its formula and only the token's value changes. G6 imports into the live sheet instead, so it
 *  rebuilds that formula: `templateFormula` is the template's band-row formula (tokens intact), `k` the
 *  band-row offset, `currentFormula` the cell's merged formula (tokens replaced by `fieldLiteral` values
 *  and relative refs shifted by `k`, as `substituteRow` wrote it). The merged literals are read back
 *  from `currentFormula`; tokens named in `overrides` (by field name, case/bracket-insensitive) get the
 *  new value. Returns the new formula (no leading "="), or null when `currentFormula` is not that
 *  template's merge output (edited, or a blob from elsewhere) — the caller then decides. */
export function resubstituteFieldTokens(
  templateFormula: string, k: number, currentFormula: string, overrides: Readonly<Record<string, string>>,
  /** The cell carries a date format, so a date value is spliced as its serial (fieldLiteral). */
  dateCell = false,
): string | null {
  const TOKEN_RE = /FIELD\("([^"]*)"\)|\[\[([^\]]+)\]\]/gi;
  const names: string[] = [];
  const PH = (i: number) => `\u0000${i}\u0000`;   // never an A1 ref, never quoted → shiftFormula leaves it
  const tpl = templateFormula.replace(/^\s*=/, '').replace(TOKEN_RE, (_m, a?: string, b?: string) => {
    names.push((a ?? b ?? '').trim()); return PH(names.length - 1);
  });
  if (!names.length) return null;
  const parts = shiftFormula(tpl, k).split(/\u0000\d+\u0000/);
  const esc = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  // A merged literal is quoted text, or a date serial (fieldLiteral on a date cell).
  const m = new RegExp(`^${parts.map(esc).join('("(?:[^"]|"")*"|-?\\d+(?:\\.\\d+)?)')}$`).exec(currentFormula.replace(/^\s*=/, ''));
  if (!m) return null;
  const want = new Map(Object.entries(overrides).map(([n, v]) => [normKey(n), v]));
  let out = parts[0]!;
  for (let i = 0; i < names.length; i++) {
    const nk = normKey(names[i]!);
    out += (want.has(nk) ? fieldLiteral(want.get(nk), dateCell) : m[i + 1]!) + parts[i + 1]!;
  }
  return out;
}

/** Value written into a pure-FIELD cell.
 *
 *  A blank field yields an EMPTY STRING cell (`''`), as DevExpress writes it — see the note at the
 *  bottom of setFieldCell (2026-09-24). The 2026-08-13 rule wrote a truly empty cell instead: at the
 *  time Univer's VALUE("") returned 0 (now overridden to #VALUE!, limsRoundFn.ts), which is what made
 *  a fresh sheet's QCStatus compute Fail/Pass; with Excel-faithful VALUE the ""-cell model blanks it
 *  through the template's own IFERROR, exactly as OG.
 *
 *  NOTE (2026-08-13): numeric fields arrive from the merge SP as TEXT
 *  ("1413", "0.566") and are written as text here. That was investigated as the
 *  cause of blank Rec %/RPD % and RULED OUT — Univer's engine coerces numeric
 *  text in arithmetic just as Excel does (verified directly:
 *  `NumberValueObject(100).divided(StringValueObject('1413'))` → 0.0707, no
 *  error). So do not "fix" this by coercing to numbers; it changes cell type and
 *  formatting for no behavioural gain. */
function cellValue(v: unknown): string | number | null {
  if (v === undefined || v === null) return null;          // truly empty, NOT ''
  if (typeof v === 'number') return v;
  const s = String(v);
  return s.trim() === '' ? null : s;                       // '' / whitespace → empty cell
}

/** FIELD names whose pure-FIELD cell MUST be written as a real NUMBER, not numeric
 *  TEXT.  DevExpress FIELD() returns text and Univer coerces numeric text in plain
 *  arithmetic (so most cells are fine as text — see the cellValue note), but the QC
 *  stat cells aggregate the result column with `AVERAGEIFS(G:G, …)` / `AVERAGEIFS`,
 *  and Excel/Univer AVERAGEIFS/SUMIFS silently IGNORE text cells.  So a text
 *  NumericResult made every sample-derived stat (DupSourceResult AE / SpikeSourceResult
 *  AG → Rec% O / RPD% P / QCStatus T for Dup/MS/MSD) blank on REOPEN, while a
 *  standalone LCSD (whose source result also feeds an AVERAGEIFS) and the plain
 *  arithmetic rows still worked — hence the "some values missing only after reopen"
 *  report.  The 2026-08-13 note "ruled out" numeric coercion by testing `divided`
 *  (arithmetic, which coerces) — it never tested AVERAGEIFS.  Verified with the real
 *  Univer engine on the template-4014 formulas: text→blank, number→computes.
 *  (fix 2026-09-09, headless repro + user OG side-by-side) */
const NUMERIC_AGG_FIELDS = new Set(['NUMERICRESULT']);

/** Merge every FIELD value as TEXT (DevExpress semantics) — except an analyst-ENTERED field on reopen
 *  and the engine outputs (see the note in setFieldCell). `false` restores the pre-2026-09-24 number
 *  typing of entry / aggregated fields. */
const FIELD_TEXT_LIKE_DEVEXPRESS = true;

/** Engine OUTPUT fields. OG's formula engine (Alpaca.FormulaEngine, see Engine B) writes Result /
 *  Rec % / RPD back into the sheet as DOUBLES, so in every saved OG blob these cells are NUMBERS
 *  under the template's own format ("0.00" → 0.07). SampleInfo stores them as text and the re-merge
 *  wrote that text back, so a reopened sheet held "0.07" as TEXT — display-equal, but a different
 *  cell type from OG (parity class 3, 2026-09-23: 308 RESULT + 81 %RECOVERY cells). A non-numeric
 *  result ("<0.25", "ND") stays text, exactly as OG's engine leaves it. */
const ENGINE_OUTPUT_FIELDS = new Set(['RESULT', '%RECOVERY', 'RPD']);

/** OG's number format for an engine-output cell: the decimals of the value's OWN text — "111" →
 *  "0", "39.9" → "0.0", "0.070" → "0.000" (BuildNumberFormat / DecimalFormat: SpreadsheetEntryController
 *  :26981 on load-time coercion and Apply Rounding, QCBatchReportingRecalc :766 on the engine stamp).
 *  The template's pattern is overridden, so the sheet shows exactly the digits the rounding engine
 *  produced and the saved blob carries the same format as OG's. (parity, 2026-09-23) */
export function decimalFormatOf(text: string): string {
  const t = text.trim();
  const dot = t.indexOf('.');
  const decimals = dot < 0 ? 0 : t.length - dot - 1;
  return decimals <= 0 ? '0' : '0.' + '0'.repeat(decimals);
}

/** The ONE date/time text shape the app puts on the wire — the merge SP's
 *  `FORMAT(…, 'MM/dd/yyyy HH:mm')` and the page's fmtLocal* stamps (ENTEREDDATE / ANALYZEDDATE /
 *  reagent ExpiredDate). Seconds are tolerated for the entry-time stamps. */
const DATE_TEXT_RE = /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?)?$/;

/** A canonical `MM/DD/YYYY[ HH:mm[:ss]]` text → a Date on the UTC axis (what ExcelJS turns into
 *  the serial OG stores — ExcelJS converts with UTC parts), else null. */
export function canonicalDateText(v: unknown): Date | null {
  if (typeof v !== 'string') return null;
  const m = DATE_TEXT_RE.exec(v.trim());
  if (!m) return null;
  const d = new Date(Date.UTC(Number(m[3]), Number(m[1]) - 1, Number(m[2]), Number(m[4] ?? 0), Number(m[5] ?? 0), Number(m[6] ?? 0)));
  return Number.isNaN(d.getTime()) ? null : d;
}

/** A TIME-only pattern (`h:mm;@`, `hh:mm:ss`) — hours / minutes / seconds, no year / month / day. The
 *  first section only; quoted literals and `[h]`-style brackets ignored. */
export function isTimeOnlyNumFmt(fmt: string | undefined): boolean {
  if (!isDateNumFmt(fmt)) return false;
  const first = (fmt!.split(';')[0] ?? '').replace(/"[^"]*"/g, '').replace(/\[[^\]]*\]/g, '');
  // An `m` next to an h or s is MINUTES; any y / d, or an m that is not beside h/s, means a date.
  if (/[yd]/i.test(first)) return false;
  return /h/i.test(first) || /s/i.test(first);
}

/** The time-of-day part of a date, on the 1899-12-30 epoch — the value OG's time-only cell holds.
 *  SampleInfo keeps a time entry as a full datetime whose date part is the SAVE day (TSS G3
 *  UserDefinedDate4 = 2026-09-10 09:42 under a 2026-09-09 date field), while the sheet the analyst
 *  typed into holds just 0.404… (09:42) — so the header `G2+G3` gives the start date-time, 46274.404,
 *  exactly as OG's blob. (2026-09-24) */
function timeOfDay(d: Date): Date {
  return new Date(Date.UTC(1899, 11, 30, d.getUTCHours(), d.getUTCMinutes(), d.getUTCSeconds()));
}

/** Is this number format a DATE / TIME pattern (`mm/dd/yyyy hh:mm`, `m/d/yy h:mm;@`, built-in 22…)?
 *  Only the first section counts and quoted literals / `[h]`-style brackets are ignored. */
export function isDateNumFmt(fmt: string | undefined): boolean {
  if (!fmt || fmt === 'General' || fmt === '@') return false;
  const first = (fmt.split(';')[0] ?? '').replace(/"[^"]*"/g, '').replace(/\[[^\]]*\]/g, '').replace(/\./g, '');
  return /[ymdhs]/i.test(first) && !/[0#?]/.test(first);
}

/** Write a resolved FIELD value into a cell, matching DevExpress `FIELD()` display.
 *
 *  DevExpress `FIELD()` returns TEXT, so a numeric field value is shown VERBATIM
 *  (general) — the cell's number PATTERN does NOT reformat it. The templates put
 *  "0.0" on reference columns (DF, Spike Amount, the limits), and Univer applies
 *  that pattern to the value, so a whole number gained a trailing decimal: Spike
 *  Amount "1413" → "1413.0", DF "1" → "1.0", where OG shows "1413" / "1". Neutralise
 *  the pattern to General for a cell that actually receives a numeric value; a blank
 *  field keeps its pattern so a number the analyst later types still formats.
 *  (fix 2026-08-13, user OG side-by-side)
 *
 *  `fieldKey` (the FIELD name) lets an AVERAGEIFS-aggregated column (NumericResult)
 *  be written as a real number rather than numeric text — see NUMERIC_AGG_FIELDS. */
/** Set a cell's number format WITHOUT touching its neighbours. On load ExcelJS hands every cell that
 *  shared one `<xf>` in the file the SAME style object, and `cell.numFmt = x` writes through to it
 *  (`this.style.numFmt = value`) — so neutralising "0.0" → General on one numeric cell also stripped
 *  the `@` from every other cell born with that style (the IsReport column on pH-MetrOhm 1027 lost
 *  its text format because a numeric entry cell elsewhere in the row shared its xf; parity class 3,
 *  2026-09-23). Cloning the style first makes the change local. */
function setNumFmt(cell: { numFmt?: string; style?: Record<string, unknown> }, fmt: string): void {
  if (cell.style && typeof cell.style === 'object') cell.style = { ...cell.style, numFmt: fmt };
  else cell.numFmt = fmt;
}

export function setFieldCell(cell: { value: unknown; numFmt?: string; style?: Record<string, unknown> }, value: unknown, fieldKey?: string, colIsAggregated = false, isEntryField = false, entryTime = true): void {
  const cv = cellValue(value);
  // PRESERVE the template cell's OWN number format. OG's DevExpress merge keeps it, so an
  // entry cell formatted "0.000" shows the merged 1 as "1.000" (MV Reading / Numeric Result —
  // client 2026-09-15). We used to overwrite it with 'General', which flattened those to "1".
  // Only fall back to General when the template carries no usable numeric format: none, the
  // catch-all 'General', or a TEXT '@' format (which would otherwise pin a real number as
  // left-aligned text). A numeric-LOOKING value in a text-formatted cell still renders
  // unformatted, matching OG (Spike Amount "1" with numFmt "0.0" stays "1").
  const tmplFmt = cell.numFmt;
  const keepFmt = !!tmplFmt && tmplFmt !== 'General' && tmplFmt !== '@';
  // A DATE/TIME value into a date-formatted cell is written as a real DATE (Excel serial + the
  // template's pattern), which is what DevExpress FIELD() does for a DateTime column — every OG blob
  // holds AnalyzedDate / EnteredDate / CollectionDate as serials under "mm/dd/yyyy hh:mm". The wire
  // carries the ONE canonical text shape (see DATE_TEXT_RE), so the runtime type is recovered from
  // the text + the cell's own format; a date text into a cell WITHOUT a date pattern stays text
  // (OG would show a bare serial there — no template does that). Parity class 3, 2026-09-23:
  // ~11 000 `type` cells across 26 templates were exactly this.
  const asDate = isDateNumFmt(tmplFmt) ? canonicalDateText(cv) : null;
  // Only an analyst-ENTERED time keeps just the time of day (`entryTime`, TSS G3 — see timeOfDay). A merged
  // date-time shown in a time-only cell stays the whole date-time, as DevExpress FIELD() writes it: THM Sequence
  // H4 "Analyzed Time" (h:mm, FIELD("ANALYZEDDATE")) holds 2026-09-02 19:36 in OG, AI held 19:36 on 1899-12-30
  // (OG vs AI THM comparison 2026-09-29). Both display 19:36.
  if (asDate) { cell.value = isTimeOnlyNumFmt(tmplFmt) && entryTime ? timeOfDay(asDate) : asDate; return; }
  // Write a real NUMBER (not numeric text) when this cell's column is cross-row aggregated by
  // AVERAGEIFS/SUMIFS (`colIsAggregated`, detected per-template from its own formulas) OR the
  // field is a known numeric-result field. Different templates bind the aggregated result to
  // DIFFERENT fields (4014 NumericResult, 1016 UserDefined14, …), so the column-driven check is
  // what makes this GLOBAL; NUMERIC_AGG_FIELDS is a belt-and-suspenders for NumericResult. The
  // Number.isFinite guard keeps genuinely-text fields (SysSampleCode/Parameter criteria columns,
  // "<RL") as text even when their column appears in an aggregation.
  //
  // `isEntryField` (REOPEN, Write=1 fields only): the analyst TYPED this value, so it must come
  // back as the NUMBER they typed — OG reopens from the saved blob where a typed cell is a
  // number and the template's "0.00" format still renders "10.00"; re-merged as text the format
  // is ignored and the reopened sheet showed "10" (user OG side-by-side 2026-09-22). Merge-only
  // reference fields (SpikeAmount / DF / RL) are NOT entry fields and stay text, exactly like
  // DevExpress FIELD() — the 2026-08-13 "don't coerce" rule still holds for them.
  const key = fieldKey ? normKey(fieldKey) : '';
  // DevExpress FIELD() writes TEXT for every merged value — every saved OG blob holds the typed inputs as
  // text (TOC E10 "0.2400", THM NumericResult "9.8", RunNo "1", Mass "63", Conductivity Reading
  // "1416.00"); only the ENGINE's outputs (Result / Rec % / RPD) are numbers there. The parity harness
  // "type" class (~12 000 cells) was G6 typing entry / aggregated fields as numbers: the 2026-08-17
  // AVERAGEIFS coercion and the 2026-09-22 "typed value comes back as the number" rule. Both reasons are
  // gone — the Dup / Spike source stats come from Engine B's write-back, not AVERAGEIFS (OG's own
  // AVERAGEIFS over text yields blank, as its blobs show), and Save keeps the displayed text ("10.00"
  // stays "10.00") so a text re-merge renders exactly what the analyst saw. (2026-09-24, global fix #3)
  //
  // EXCEPT an analyst-ENTERED field on reopen (`isEntryField`, DataParsing Write=1, only passed when the
  // reopen feed supplies the entry set). OG reopens from its saved blob, and there a value TYPED in the
  // grid is a NUMBER — only imported / merged values are text. Formulas are written for the typed number:
  // Colilert 8103 AE `IF(H11=1, J$6, …)` (OG "09/15/2026 12:36", G6 lost the time with "1" as text),
  // the Dup/LCSD/MSD `AVERAGEIFS` parent lookups (8088 AP). Owner decision 2026-09-26 (option B): numbers
  // on reopen. Harness: ~230 wrong/blank cells fixed (8103, 8074, 9120, 8076, 8085, 8088, 8096, 8101);
  // the cost is type-only, display-equal cells where OG's value came from an import (8070, 8062, 8112,
  // 1014, 8115). Numeric-looking text only (Number.isFinite below) — "<0.25", "ND" stay text.
  const typedAsNumber = FIELD_TEXT_LIKE_DEVEXPRESS
    ? (ENGINE_OUTPUT_FIELDS.has(key) || isEntryField)
    : (colIsAggregated || isEntryField || NUMERIC_AGG_FIELDS.has(key) || ENGINE_OUTPUT_FIELDS.has(key));
  if (cv !== null && Number.isFinite(Number(cv)) && typedAsNumber) {
    cell.value = Number(cv);
    // NOTE: an engine-output cell keeps the TEMPLATE pattern here. OG's load-time CoerceNumericCell
    // (:26739) re-formats only a cell that arrived as TEXT; a value the ENGINE stamped keeps the
    // stamp's format (DecimalFormat) — which the Engine B write-back applies (decimalFormatOf) —
    // and everything else keeps the template's. Applying the value-decimals rule here moved the
    // residue instead of removing it (1009: 12 cells either way, 2026-09-23).
    if (!keepFmt) setNumFmt(cell, 'General');
    return;
  }
  // A BLANK field becomes an EMPTY-STRING cell, exactly as DevExpress FIELD() writes it — every OG blob
  // (old and new builds, every template: TOC M10 SpikeAmount, N10/R10 Rec %/RPD, Conductivity K9
  // SpikeAmount, the CtrlLimit helpers) holds "" there, never a truly empty cell — and DevExpress'
  // ISBLANK treats that cell as NOT blank (TOC's QCStatus computes Pass/Fail on the Blank rows in OG's
  // own blob, past the `OR(ISBLANK(R10), ISBLANK(N10), …)` gate). The 2026-08-13 null-cell rule was
  // built on the belief that OG's cells were truly empty; the harness (parity/) showed the opposite
  // (QCStatus blank in G6, "Pass" in OG — 6 cells on the first run, the VOC CCV Pass→Fail class).
  // ISBLANK / _LIMSEMPTY (limsRoundFn.ts) and the CF gate carry the matching semantics; VALUE("") is
  // #VALUE! there, so a fresh sheet's QCStatus still blanks through its IFERROR, as OG's does.
  // (2026-09-24, global fix for the parity class "empty field cells")
  // …and a NUMBER the data source sends (RunNo 1, Mass 63, DF 1) is TEXT too — FIELD() always returns text
  // (OG blobs: RunNo "1", Mass "63"; the harness "type" class — PFAS AF 1 100 cells per batch). (2026-09-24)
  const written = cv === null ? '' : FIELD_TEXT_LIKE_DEVEXPRESS && typeof cv === 'number' ? String(cv) : cv;
  cell.value = written;
  // A TEXT value never needs 'General' — Excel/Univer show text verbatim whatever the pattern — so the
  // template's own '@' (text) format is kept, as DevExpress keeps it (parity class 3: "-1" flags under
  // '@' came back as General). A real NUMBER still neutralises an unusable ('@' / none) format, and a
  // numeric-looking text only gets General when the cell has no format at all.
  if (written !== '' && !keepFmt && (typeof written === 'number' || (!tmplFmt && Number.isFinite(Number(written))))) setNumFmt(cell, 'General');
}

/** Normalize a merge-field / column key for comparison: uppercase and strip ONE
 *  pair of surrounding square brackets.  The Alkalinity-style templates bind the
 *  control-limit helper cells via `FIELD("[%RPDULIMIT]")` — the field name itself
 *  carries brackets — while the SP emits the column as `%RPDULIMIT` (SQL strips the
 *  `[...]` alias delimiter).  Without this the four Rec/RPD limit fields never bind,
 *  so the RPD/Rec CtrlLimit columns render `[[%RPDULIMIT]]` / `#VALUE!`. (fix 2026-08-05) */
function normKey(k: string): string {
  return k.toUpperCase().replace(/^\[(.+)\]$/, '$1');
}

/** Per-merge index of each row's keys by normKey (speed, 2026-09-30). A reopen record carries every
 *  SampleInfo column (~440 keys), and `lookup` used to normKey-scan all of them for every FIELD cell of
 *  every row — tens of millions of regex calls on a VOC batch. The index holds the FIRST key of each
 *  normalised name, exactly what the scan returned first. It lives only while a merge runs
 *  (mergeSdmsTemplateWorkbook sets and clears it), because a caller may add keys to its records between
 *  two merges (the edit handoff's entered-results overlay) and a stale index would miss them. */
let keyIndexCache: WeakMap<object, Map<string, string>> | null = null;
function keyFor(o: Row, K: string): string | undefined {
  if (!keyIndexCache) { for (const kk of Object.keys(o)) if (normKey(kk) === K) return kk; return undefined; }
  let idx = keyIndexCache.get(o);
  if (!idx) {
    idx = new Map();
    for (const kk of Object.keys(o)) { const n = normKey(kk); if (!idx.has(n)) idx.set(n, kk); }
    keyIndexCache.set(o, idx);
  }
  return idx.get(K);
}

/** Case-insensitive, bracket-insensitive lookup: record wins, then the batch header, then blank.
 *  A name with NO matching column at all (≠ a present-but-null value) is recorded in
 *  `missing` — after the merge those are console-warned as template↔data drift. */
function lookup(key: string, rec: Row, headerUpper: Row, missing?: Set<string>): unknown {
  const K = normKey(key);
  const rk = keyFor(rec, K);
  if (rk !== undefined) return rec[rk];
  const hk = keyFor(headerUpper, K);
  if (hk !== undefined) return headerUpper[hk];
  if (missing && !ENTRY_FIELDS.has(K) && !ENTRY_FIELD_RE.test(K)) missing.add(key);
  return undefined;
}

/** The FIRST cell each pure FIELD landed in on EVERY sheet, beside the caller's `fieldRefs` object (which keeps
 *  the first sheet only): field → sheet → A1. */
const refsBySheet = new WeakMap<object, Map<string, Map<string, string>>>();

/** Every sheet's cell (`Sheet!A1`) the merges into `fieldRefs` put `field` in — one per sheet, in merge order.
 *  A Model-1 template repeats its header on each run sheet (VOC 8086: QC Batch ID at J5 on CCV1, Method Blank1,
 *  …), and `fieldRefs` alone names only the first. Falls back to that one ref for a map no merge filled. */
export function fieldRefCells(fieldRefs: Record<string, string>, field: string): string[] {
  const key = field.toUpperCase();
  const sheets = refsBySheet.get(fieldRefs)?.get(key);
  if (sheets?.size) return [...sheets].map(([sheet, a1]) => `${sheet}!${a1}`);
  return fieldRefs[key] ? [fieldRefs[key]!] : [];
}

/** Merge the template with the batch records → the merged .xlsx as an ArrayBuffer. */
export async function mergeSdmsTemplate(...args: Parameters<typeof mergeSdmsTemplateWorkbook>): Promise<ArrayBuffer> {
  return writeSdmsWorkbook(await mergeSdmsTemplateWorkbook(...args));
}

/** The merge, leaving the result IN MEMORY — the open chain edits it further and writes it once
 *  (sdmsWorkbookIo.ts). `mergeSdmsTemplate` is this plus the write. */
export async function mergeSdmsTemplateWorkbook(
  templateBuf: ArrayBuffer,
  header: Row,
  records: Row[],
  /** Sub-sheet datasets — the "Reagents" / "Instruments" sheets expand their OWN detail
   *  band against these (OG GenerateMailMerge feeds each sheet its DataMember:
   *  ReagentDataSource / InstrumentDataSource). Empty → that sheet stays header-only. */
  reagents: Row[] = [],
  instruments: Row[] = [],
  /** OUT — where each header FIELD landed, as `Sheet!A1`, keyed by the UPPERCASE
   *  field name. A header value that is not known until AFTER the merge (QCBATCHID:
   *  the id is minted by the save) can then be stamped into its cell instead of
   *  leaving the blank the merge wrote (client 2026-08-19). */
  fieldRefs?: Record<string, string>,
  /** The template's Traceability setting — OG merges the "Reagents" / "Instruments"
   *  sheets ONLY when it names them (GenerateMailMerge :3629 / :3650); null = neither. */
  traceability: string | null = null,
  /** `SpreadSheetBuilder_TemplateInfo.Model` (OG EnumModel): 0 = BatchSampleEntry — all runs
   *  on the one main sheet; 1 = SampleSheetNavigation — one worksheet PER RUN named by its
   *  sample code (VOC 8086); 2 = CalibrationSet -- one worksheet PER calibration LEVEL
  *  named `Level{n}` (PFAS Calibration 8110), cloned from the main sheet. */
  model: number = 0,
  /** REOPEN only — the template's analyst-ENTERED field names (DataParsing Write=1). Their
   *  numeric values are written as real NUMBERS so the template's number format renders the
   *  decimals the analyst saw (OG blob parity, see setFieldCell). Omitted on a fresh merge. */
  entryFields: readonly string[] | null = null,
  /** The Write=1 fields DataParsing marks Continuous but places in a HEADER cell (VOC 8086 Remark at B6 — the
   *  server's SdmsSheetFields.ContinuousHeaderFields). OG's save stores such a field per band row from its
   *  column, so a record's value is the column's (the run's QC type), never the header cell's: those header
   *  cells take the batch header only, not the records. Null / empty = none. (2026-09-30) */
  continuousHeaderFields: readonly string[] | null = null,
  /** Merge ONLY these non-run sheets (by exact name) — for a caller that copies just them out of the result (the
   *  instrument import refreshing Sequence / Summary). The per-run / per-level sheets are then not built at all,
   *  which is most of a Model-1 merge (VOC 8086: 13 run sheets, ~7 s of the import, 2026-10-09). The named sheets
   *  merge exactly as in a full merge — their dataset does not depend on the run sheets. A name that IS the main
   *  sheet falls back to the full merge. Null = every sheet. */
  onlySheets: ReadonlySet<string> | null = null,
): Promise<import('exceljs').Workbook> {
  const prevKeyIndex = keyIndexCache;
  keyIndexCache = new WeakMap();
  try {
    return await mergeInto(templateBuf, header, records, reagents, instruments, fieldRefs, traceability, model, entryFields,
      new Set((continuousHeaderFields ?? []).map(normKey)), onlySheets);
  } finally {
    keyIndexCache = prevKeyIndex;
  }
}

async function mergeInto(
  templateBuf: ArrayBuffer, header: Row, records: Row[], reagents: Row[], instruments: Row[],
  fieldRefs: Record<string, string> | undefined, traceability: string | null, model: number,
  entryFields: readonly string[] | null, headerOnly: ReadonlySet<string>,
  onlySheets: ReadonlySet<string> | null = null,
): Promise<import('exceljs').Workbook> {
  const ExcelJS = (await import('exceljs')).default;
  const wb = new ExcelJS.Workbook();
  // DevExpress stores a formula against a sheet the template lacks (RawDataSheet, a Model-1 run sheet
  // such as 'CCV1') as a path-missing EXTERNAL ref `[n]Name!` and reads it back as plain `Name!` —
  // resolve it the same way before ExcelJS sees it (sdmsMissingSheetRefs.ts).
  await wb.xlsx.load(await resolveMissingSheetRefs(templateBuf));
  // Each template sheet's AutoFilter, read before any sheet is copied / expanded (see the AutoFilter step).
  const tplFilters = new Map<string, NonNullable<import('exceljs').Worksheet['autoFilter']>>();
  for (const w of wb.worksheets) if (w.autoFilter) tplFilters.set(w.name, structuredClone(w.autoFilter));
  // The analyst-ENTRY set is CONFIG-DERIVED on the server (SdmsMergeService.DeriveEntryFieldsAsync):
  // DataParsing Write=1 minus the columns the fresh merge SP itself supplies (learned from the SP's
  // own schema) minus the save-rounded columns — so a template that enters a field this file never
  // heard of (VolumeUsed, Vt, a UserDefinedDate…) is handled with no code change. It drives BOTH the
  // reopen numeric typing (a typed number comes back as a number so the template format renders it)
  // and the drift warning below (an entry field is legitimately blank at merge). The hand-kept
  // ENTRY_FIELDS list is now only the FALLBACK for a caller that passes no entryFields (calibration
  // merges, tests). (2026-09-22)
  const entrySet = new Set((entryFields ?? []).map(normKey));
  const isEntry = (k: string): boolean =>
    entryFields ? entrySet.has(k) : (ENTRY_FIELDS.has(k) || ENTRY_FIELD_RE.test(k));

  const headerUpper: Row = {};
  for (const k of Object.keys(header)) headerUpper[k.toUpperCase()] = header[k];
  // Template↔data drift detector: FIELD names the template references that NO
  // record/header column supplies (see lookup). Warned once, deduped, after the
  // merge — a silent blank column is a data-accuracy bug, not a cosmetic one.
  const missing = new Set<string>();

  // ── Per-sheet dataset — EXACTLY as OG GenerateMailMerge binds MailMergeDataMember by
  // sheet NAME (SpreadsheetEntryController.cs :3605-3688), including its exclusions (:3689).
  // Sheet names match EXACTLY (case-sensitive), as OG's `== "…"` does:
  //   "Calibration"                → OG excludes it from the FIELD merge (intCalibrationSheetID;
  //                                  the calibration routine fills it) — header-only here so a
  //                                  stray FIELD can't render #NAME? (4014 has one: CALIBRATIONID)
  //   "Sequence"                   → SequenceTableDataSource = DISTINCT(dtsample) over the run
  //                                  columns, minus the THMs/THAA5 parameter rows (:3541-3547)
  //   "Summary" / "ISTD"           → SampleParaneterTableDataSource = a straight COPY of dtsample
  //                                  (:3549-3550) → the very same `records`
  //   "Parameter-Oriented Summary" → excluded (:3689); 0 FIELDs in every template → header-only
  //   "Sample-Oriented Summary"    → excluded (:3689); same
  //   "Reagents"                   → ReagentDataSource ONLY if Traceability names it (:3629),
  //                                  else OG `continue`s past the sheet → header-only (no rows)
  //   "Instruments"                → InstrumentDataSource ONLY if Traceability names it (:3650)
  //   "Raw Data"                   → RawDataSummaryDataSource = imported instrument raw data,
  //                                  which a NEW batch never has (:3674-3677 null source) → header-only
  //   anything else (index 0 too) → RawDataTableDataSource = `records` (:3686-3687) — this is
  //                                  OG's DEFAULT, so an unnamed extra sheet (8112 "Summary2",
  //                                  the IDEXX/HPC MPN tables) is band-expanded, not skipped.
  const trace    = traceability ?? '';
  const sequence = model === 2 ? buildCalibSequenceDataset(records) : buildSequenceDataset(records);
  const HEADER_ONLY: Row[] | null = null;
  const datasetFor = (name: string): Row[] | null => {
    switch (name) {
      case 'Calibration':
      case 'Parameter-Oriented Summary':
      case 'Sample-Oriented Summary':
      case 'Raw Data':          return HEADER_ONLY;
      case 'Sequence':          return sequence;
      case 'Summary':
      case 'ISTD':              return records;
      case 'Reagents':          return trace.includes('Reagents')    ? reagents    : HEADER_ONLY;
      case 'Instruments':       return trace.includes('Instruments') ? instruments : HEADER_ONLY;
      default:                  return records;
    }
  };

  // ── OG EnumModel.SampleSheetNavigation (SpreadSheetBuilder_TemplateInfo.Model = 1) ─────
  // OG's GenerateMailMerge always merges ONE RUN AT A TIME (:3562) and adds each result to
  // the workbook under the run's sample code (:3699 / :3817). For a Model=1 template (VOC
  // 8086 — verified on all 18 of its saved batches) DevExpress keeps those results as separate
  // worksheets, so the batch opens as one tab per run and the template's main sheet is gone;
  // Model 0 collapses them into the single main sheet (the loop below). We reproduce Model=1
  // explicitly: copy the PRISTINE main sheet once per run, expand each copy with ONLY that
  // run's rows, name it by the run code, then drop the original. Sub-sheets (Sequence /
  // Summary / Reagents / Instruments) stay single, exactly as OG (:3817 adds by name once).
  const main = wb.worksheets[0];
  const mainName = main?.name ?? '';
  const runSheetNames = new Set<string>();
  // A partial merge never needs the run sheets — unless the main sheet itself is asked for.
  const only = onlySheets && !onlySheets.has(mainName) ? onlySheets : null;
  const buildRuns = only === null;
  if (buildRuns && model === 1 && main && datasetFor(main.name) === records && records.length) {
    const runOf = (r: Row) => String(lookup('SYSSAMPLECODE', r, {}) ?? '');
    const codes: string[] = [];
    for (const r of records) { const c = runOf(r); if (c && !codes.includes(c)) codes.push(c); }
    const used = new Set<string>(wb.worksheets.map(w => w.name));
    for (const code of codes) {
      const name = sheetNameFor(code, used); used.add(name);
      const ws = wb.addWorksheet(name);
      copySheet(main, ws);
      runSheetNames.add(name);
      expandSheet(ws, records.filter(r => runOf(r) === code), headerUpper, missing, fieldRefs, 5, entrySet, isEntry, headerOnly);
    }
    wb.removeWorksheet(main.id);
    orderAsOgAccumulates(wb, runSheetNames);
  }

  // -- OG EnumModel.CalibrationSet (Model = 2) -- one worksheet PER calibration LEVEL --------
  // OG's GenerateMailMerge CalibrationSet branch (SpreadsheetEntryController.cs :4052-4262)
  // loops over each distinct LEVELNO, merges the template's MAIN sheet with only that level's
  // rows, and renames the result "Level"+LEVELNO; the shared sub-sheets (Sequence / Summary /
  // ISTD / Regression Curves) accumulate ONCE. We reproduce it like the Model=1 path but keyed
  // on LEVELNO and named Level{n}; the tab order is OG's (Sequence, Summary, Regression Curves,
  // ISTD moved to 0..3 — sdmsSheetOrder.ts; before 2026-09-28 the sub-sheets kept their template
  // order ahead of the Level tabs). Fed by AI_Analysis_Sdms_CalibrationMergeData_SP (one row per
  // parameter x level). NOTE: OG binds "Regression Curves" to the level-1 subset only; we bind
  // ALL records to it -- flagged, harmless for a blank new set. (built 2026-09-07)
  if (buildRuns && model === 2 && main && datasetFor(main.name) === records && records.length) {
    const levelOf = (r: Row) => String(lookup('LEVELNO', r, {}) ?? '');
    const levels: string[] = [];
    for (const r of records) { const lv = levelOf(r); if (lv && !levels.includes(lv)) levels.push(lv); }
    levels.sort((a, b) => Number(a) - Number(b));
    const used = new Set<string>(wb.worksheets.map(w => w.name));
    for (const lv of levels) {
      const name = sheetNameFor('Level' + lv, used); used.add(name);
      const ws = wb.addWorksheet(name);
      copySheet(main, ws);
      runSheetNames.add(name);
      expandSheet(ws, records.filter(r => levelOf(r) === lv), headerUpper, missing, fieldRefs, 5, entrySet, isEntry, headerOnly);
    }
    wb.removeWorksheet(main.id);
    orderAsOgAccumulates(wb, runSheetNames);
  }

  for (const sheet of wb.worksheets) {
    if (runSheetNames.has(sheet.name)) continue;   // merged per run above
    if (only && !only.has(sheet.name)) continue;   // a partial merge: not asked for
    const ds = datasetFor(sheet.name);
    if (ds !== null) {
      // minBand guards band detection: a `records`-fed sheet (main / Summary / default) also
      // holds a few header FIELDs, so demand ≥5 to be sure the row is the band; a named
      // sub-sheet's lone band row carries all its FIELDs, so any FIELD row is it. An EMPTY
      // dataset still expands once (one blank band row) + trims below — the pre-existing
      // main-sheet behaviour, kept identical.
      expandSheet(sheet, ds, headerUpper, missing, fieldRefs, ds === records ? 5 : 1, entrySet, isEntry, headerOnly);
    } else {
      // Excluded / gated-off / unsourced sheet: substitute every FIELD once so none render
      // as #NAME? (header values where they apply, blank otherwise). OG leaves these sheets'
      // FIELDs to a later routine; the blank is the faithful "no data" state here.
      for (let r = 1; r <= sheet.rowCount; r++) substituteRow(sheet.getRow(r), {}, headerUpper, 0, missing, false, fieldRefs, sheet.name, undefined, undefined, isEntry);
    }
  }

  // Drift = referenced by the template, supplied by nothing, and NOT an analyst-entry field
  // (those are legitimately blank until typed). `missing` was pre-filtered by the hand list in
  // lookup(); apply the config-derived set on top so a derived entry field never warns.
  const drift = [...missing].filter(k => !isEntry(normKey(k)));
  if (drift.length) {
    console.warn(
      '[SDMS merge] template FIELD name(s) with NO matching data column — these cells rendered BLANK. '
      + 'Either the merge SP must emit the column or the name is a template typo: '
      + drift.sort().join(', '));
  }

  // ── Hide the columns OG never shows: right of each sheet's DETAILRANGE ─────
  // OG's DevExpress merge copies ONLY the HEADERRANGE/DETAILRANGE named ranges
  // into its output workbook, so a per-template config block sitting to the
  // right of the band (pH 1027 keeps a SigFig/CutOff/Decimal table at AF..AN)
  // never appears in OG at all. Mark those columns hidden so every downstream
  // view treats them exactly like the template's own hidden helper columns.
  // Read from the RAW template zip: ExcelJS collapses same-named sheet-local
  // defined names on load (only one sheet's DETAILRANGE survives its model), so
  // the parsed workbook can't answer this. Templates without the name (Metals
  // 8070) are untouched. (user pH side-by-side 2026-08-17)
  try {
    const ends = await detailRangeEndCols(templateBuf);
    for (const sheet of wb.worksheets) {
      // A per-run tab is a copy of the main sheet, so it takes the main sheet's DETAILRANGE end.
      const end = ends[sheet.name] ?? (runSheetNames.has(sheet.name) ? ends[mainName] : undefined);
      if (!end) continue;
      for (let c = end + 1; c <= sheet.columnCount; c++) sheet.getColumn(c).hidden = true;
      // …and their cells are DROPPED, as OG's output never has them: VOC 8086's "Result Rounding" SigFig/CutOff/
      // Decimal block at BC..BK was in every AI run sheet and in no OG one (OG vs AI comparison 2026-09-28,
      // point 12). Nothing reads those cells (the rounding settings come from the Testparameter).
      sheet.eachRow({ includeEmpty: true }, row => { if (row.cellCount > end) row.splice(end + 1, row.cellCount - end); });
    }
  } catch { /* malformed defined names — leave the columns as authored */ }

  // ── AutoFilter, as OG: it re-applies each template sheet's AutoFilter to the generated sheet
  // (SpreadsheetEntryController :3745-3756), where it covers the header row AND the data block under it —
  // OG's VOC run sheets A7:AQ68, Sequence B9:H17, Summary B9:J497. AI's run / Sequence sheets had none and
  // Summary kept the header-only B9:J9 (point 12). A per-run / per-level tab takes the main sheet's filter.
  for (const sheet of wb.worksheets) {
    const src = tplFilters.get(sheet.name) ?? (runSheetNames.has(sheet.name) ? tplFilters.get(mainName) : undefined);
    const ref = src ? filterRefOver(sheet, src) : null;
    if (ref) sheet.autoFilter = ref;
  }

  // Tab order, as OG's open path leaves it (sdmsSheetOrder.ts) — the summaries before Reagents on VOC.
  applyOgSheetOrder(wb, model);

  // The caller writes it: writeSdmsWorkbook restores the conditional-formatting `text=` attribute
  // ExcelJS drops on write (without it DevExpress / ALPACA refuses the workbook — sdmsCfTextRepair.ts).
  return wb;
}

/** Worksheet tab order. ExcelJS honours `orderNo` on write but omits it from its typings. */
function setOrder(ws: import('exceljs').Worksheet, n: number): void {
  (ws as unknown as { orderNo: number }).orderNo = n;
}

/** The order OG's per-run / per-level merge builds its workbook in, before its moves: the FIRST run's merge
 *  result (run 1, then the template's other sheets in template order), then each later run appended
 *  (:3812-3827 / :4204-4219 add only the sheets not already there). */
function orderAsOgAccumulates(wb: import('exceljs').Workbook, runSheetNames: ReadonlySet<string>): void {
  const runs = [...runSheetNames];
  const subs = wb.worksheets.filter(w => !runSheetNames.has(w.name)).map(w => w.name);
  [...runs.slice(0, 1), ...subs, ...runs.slice(1)].forEach((name, i) => { const ws = wb.getWorksheet(name); if (ws) setOrder(ws, i); });
}

/** Excel-legal worksheet name for a run code: strip the forbidden `[]:*?/\` characters, cap
 *  at 31 chars, and de-duplicate against the names already in the workbook. */
function sheetNameFor(code: string, used: Set<string>): string {
  const base = code.replace(/[[\]:*?/\\]/g, '').trim().slice(0, 31) || 'Run';
  let name = base, n = 2;
  while (used.has(name)) { const suf = ` (${n++})`; name = base.slice(0, 31 - suf.length) + suf; }
  return name;
}

/** Copy one worksheet's content into another — ExcelJS has no clone. Carries column
 *  widths/hidden flags, every row's values (formulas included), cell styles, row heights and
 *  merged ranges: everything the per-run band expansion needs to look like the template. */
/** Verbatim worksheet copy (columns, row heights, cell values + styles, merges) — the
 *  ExcelJS equivalent of OG's `Worksheet.CopyFrom`. Exported so the Attach-Calibration
 *  embed (sdmsAttachCalibration.ts) copies a calibration's sheet into a batch workbook
 *  the same way the per-run / per-level merge does. */
export function copySheet(src: import('exceljs').Worksheet, dst: import('exceljs').Worksheet): void {
  src.columns?.forEach((c, i) => {
    const d = dst.getColumn(i + 1);
    if (c.width) d.width = c.width;
    if (c.hidden) d.hidden = true;
  });
  src.eachRow({ includeEmpty: true }, (row, r) => {
    const dr = dst.getRow(r);
    dr.values = row.values;
    if (row.height) dr.height = row.height;
    row.eachCell({ includeEmpty: true }, (cell, c) => { dst.getCell(r, c).style = cell.style; });
  });
  const merges = (src.model as { merges?: string[] }).merges ?? [];
  for (const m of merges) { try { dst.mergeCells(m); } catch { /* invalid/overlapping — skip */ } }
  // The AutoFilter travels too — a sheet swapped in by copy (the import's Sequence refresh) lost it.
  if (src.autoFilter) dst.autoFilter = structuredClone(src.autoFilter);
  // Conditional formats + data validations travel with the sheet, as OG's Worksheet.CopyFrom does. The
  // Model-1 run sheets (VOC 8086: one tab per run) are copies of the template's main sheet, and without
  // these their Rec % / QCStatus colours were gone on every tab (user report 2026-09-28; OG: ~250 rules
  // per run sheet). Deep copies — the source sheet is dropped / reused after this.
  type WithCf = { conditionalFormattings?: unknown[] };
  const cfs = (src as unknown as WithCf).conditionalFormattings;
  if (Array.isArray(cfs) && cfs.length) (dst as unknown as WithCf).conditionalFormattings = structuredClone(cfs);
  type WithDv = { dataValidations?: { model?: Record<string, unknown> } };
  const dvs = (src as unknown as WithDv).dataValidations?.model;
  const dstDv = (dst as unknown as WithDv).dataValidations;
  if (dvs && dstDv && Object.keys(dvs).length) dstDv.model = { ...(dstDv.model ?? {}), ...structuredClone(dvs) };
}

/** Calibration "Sequence" sheet dataset (OG :4057): DISTINCT by LEVELNO over the
 *  sequence columns -- one row per level so the Sequence tab lists Level 1..N. */
function buildCalibSequenceDataset(records: Row[]): Row[] {
  const seen = new Set<string>();
  const out: Row[] = [];
  for (const r of records) {
    const lv = String(lookup('LEVELNO', r, {}) ?? '');
    if (!lv || seen.has(lv)) continue;
    seen.add(lv);
    out.push(r);
  }
  return out;
}

/** The columns OG's Sequence dataset keeps — `dtSequence.DefaultView.ToTable(true, …)`,
 *  SpreadsheetEntryController :3551, in OG's order. */
const SEQUENCE_COLUMNS = ['USERDEFINED9', 'ANALYZEDDATE', 'USERDEFINED10', 'USERDEFINED11', 'TEST', 'METHOD', 'MATRIX',
  'INSTRUMENT', 'QCBATCHID', 'ANALYZEDBY', 'ENTEREDDATE', 'ENTEREDBY', 'USERDEFINED2', 'SYSSAMPLECODE', 'QCTYPE',
  'USERDEFINED13', 'USERDEFINED14', 'USERDEFINED15', 'USERDEFINED12', 'USERDEFINED1', 'SHEETNAME', 'USERDEFINED5', 'USERDEFINED7'];
/** What makes a Sequence row distinct here — see buildSequenceDataset. */
const SEQUENCE_KEY = ['SYSSAMPLECODE', 'QCTYPE', 'SHEETNAME', 'ANALYZEDDATE'];

/** OG's SequenceTableDataSource (SpreadsheetEntryController.cs :3541-3551): dtsample minus the
 *  THMs / THAA5 parameter rows, then DISTINCT over the run-level columns — i.e. ONE row per
 *  sequence entry (sample / QC run), collapsing its per-parameter rows. Columns the records lack
 *  (TEST / METHOD / … in a fresh merge) resolve from the batch header (lookup's fallback).
 *  SHEETNAME = the run's raw-data file name, blank until an import fills it (OG's select SP
 *  returns SheetName = ''). Record order (= the SP's) is preserved. */

function buildSequenceDataset(records: Row[]): Row[] {
  const EXCLUDED_PARAMS = new Set(['THMS', 'THAA5']);
  const out: Row[] = [];
  const seen = new Set<string>();
  for (const r of records) {
    const param = String(lookup('PARAMETER', r, {}) ?? '').trim().toUpperCase();
    if (EXCLUDED_PARAMS.has(param)) continue;
    // OG takes DISTINCT over all these columns AS THEY WERE WHEN IT LAST MERGED; today's rows also hold
    // values written later (VOC 8086 QB26091819_060 Method Blank1: UserDefined15 = 7.18 on its ISTD row
    // only), so a DISTINCT over every column split runs OG lists once. A row is therefore one run —
    // sample, QC type, data file and analysed date (SEQUENCE_KEY) — with the other columns from its first
    // record. That keeps OG's LOQ1 pair (QB26092507_060: one analyte re-analysed a day later) and gives
    // each run its own AcqTime (it came out as the batch date for every run). (2026-09-28)
    const row: Row = {};
    for (const c of SEQUENCE_COLUMNS) {
      const v = lookup(c, r, {});
      if (v !== undefined) row[c] = v;
    }
    // SHEETNAME: the record's own (the reopen SP emits the sample's raw-data file name once an
    // instrument file is attached — OG's dtsample.SheetName; parity class 6), else BLANK as in OG
    // (the sample-code fallback showed the code where OG's DataFile is empty, 2026-09-28).
    row.SHEETNAME = String(row.SHEETNAME ?? '').trim();
    const key = SEQUENCE_KEY.map(c => String(row[c] ?? '')).join('\u0000');
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(row);
  }
  return out;
}


/** RESTORED 2026-10-01 (user: "bring back the test-name title for a duplicated row 1") — removed 2026-09-26 to
 *  match OG, which leaves row 1 as the template has it. The GoLive PFAS 533 template still repeats the column
 *  captions in row 1, so a second "SysSampleCode" header showed above the real one.
 *
 *  G5 froze a COPY of the detail-range header at worksheet row 1 so the column captions
 *  stayed visible while scrolling; G6 doesn't want that duplicate showing as a second header.
 *  Rather than hide row 1, REPLACE its content with a centered TITLE banner — the test name —
 *  so the sheet reads like OG's other templates (e.g. Colilert, whose row 1 already carries a
 *  "9223B-Colilert 18 Quantitray" banner). Never DELETE the row — deleting shifts every row up
 *  and ExcelJS would NOT re-point the QC formulas (Rec%/RPD%/QCStatus); overwriting row 1 in
 *  place keeps every data row where the formulas expect it. Freeze panes are left as-is (user
 *  decision 2026-09-11). When no title is available it falls back to HIDING row 1 (SdmsUniverSheet
 *  maps ExcelJS row.hidden → Univer hd:1, :1095). Applies to every band-expanded sheet (main +
 *  per-run/per-level copies) via expandSheet.
 *
 *  Guard — act ONLY when row 1 is the frozen duplicate, never a title/metadata/other row.
 *  The frozen copy reproduces the header's VISIBLE leading columns but NOT the helper/config
 *  columns the template keeps off to the right of the band (Metals ICP MS 8070: row 1 = cols
 *  1..60, the header row = cols 1..72, the extra 61..72 being SigFig/CutOff/Decimal + Adjusted/
 *  Normalized helpers). So row 1 is a column-for-column SUBSET of the header row, not an exact
 *  copy. Act iff: (a) it carries at least a few captions; (b) it includes the header's FIRST
 *  column (a frozen copy is left-aligned to the header); and (c) every one of its captions equals
 *  the header's caption in the SAME column. A blank spacer, a one-cell title banner, a metadata
 *  row (Test/Method/…), a renamed caption, or a row 1 wider than the header all fail this and are
 *  left untouched — templates without the duplicate keep their own row 1. (2026-09-11) */
function titleizeDuplicateTopHeader(ws: import('exceljs').Worksheet, band: number, title: string): void {
  const headerRow = band - 1;               // the detail-range header sits directly above the band
  if (headerRow <= 1) return;               // band at row 2 → row 1 IS the header; nothing above it
  const text = (c: import('exceljs').Cell): string => {
    const v = c.value as unknown;
    if (v == null) return '';
    if (typeof v === 'object') {
      const o = v as { richText?: Array<{ text?: string }>; text?: string; result?: unknown };
      if (Array.isArray(o.richText)) return o.richText.map(t => t.text ?? '').join('').trim().toUpperCase();
      if (typeof o.text === 'string')  return o.text.trim().toUpperCase();
      if (o.result != null)            return String(o.result).trim().toUpperCase();
      return '';
    }
    return String(v).trim().toUpperCase();
  };
  const capsOf = (r: number): Map<number, string> => {
    const m = new Map<number, string>();
    ws.getRow(r).eachCell({ includeEmpty: false }, (c, col) => { const t = text(c); if (t) m.set(col, t); });
    return m;
  };
  const top = capsOf(1);
  const hdr = capsOf(headerRow);
  if (top.size < 3 || hdr.size === 0) return;                 // too few captions / no header → not the frozen copy
  if (!top.has(Math.min(...hdr.keys()))) return;              // frozen copy is left-aligned to the header's first column
  // The frozen top copy and the detail-range header label the sample/QC TYPE column with two
  // conventional captions — "QCType" on the top copy vs "SampleType" on the detail header (seen on
  // PFAS_533 / EPA 533). It is the same column, so normalise that synonym before comparing; without
  // this the single caption difference defeats the duplicate match and row 1 stays doubled. ANY
  // OTHER caption difference still keeps row 1 visible (deliberate strictness). (2026-09-17)
  const CAPTION_SYNONYM: Record<string, string> = { SAMPLETYPE: 'QCTYPE' };
  const norm = (s: string): string => CAPTION_SYNONYM[s] ?? s;
  for (const [col, t] of top) {                               // every row-1 caption must match the header in the SAME column
    const h = hdr.get(col);
    if (h === undefined || norm(h) !== norm(t)) return;
  }

  // It IS the frozen duplicate. Clear its captions, then either write the title banner or hide.
  const r1 = ws.getRow(1);
  const maxCol = Math.max(...top.keys());
  for (let c = 1; c <= maxCol; c++) r1.getCell(c).value = null;
  const t = (title ?? '').trim();
  if (!t) { r1.hidden = true; return; }                       // no test name to show → hide (old behaviour)
  // Merge across the VISIBLE leading columns so the centered title sits over the visible area
  // (the helper columns the template hides sit past this) — mirrors OG's own row-1 title merge.
  let lastVisible = 1;
  for (let c = 1; c <= maxCol; c++) if (!ws.getColumn(c).hidden) lastVisible = c;
  // Style it as the SAME title banner other templates carry in row 1 — verified across the
  // canonical set (Anions 1014 / Turbidity 1009 / Calcium 8067 / Colilert 8074): Calibri ~18
  // bold, centered+middle, NO fill, a thin black box, row height ~33. (Metals/8070's row 1 was
  // header-styled — small font + header fill — so we overwrite the style, not just the text.)
  const thin = { style: 'thin', color: { argb: 'FF000000' } } as import('exceljs').Border;
  const a1 = r1.getCell(1);
  a1.value = t;
  // Assign a FRESH whole-style object — NOT a1.font=/a1.fill=/… one property at a time. ExcelJS
  // shares ONE style object across cells with identical formatting, and here A1 (the frozen dup)
  // and the real header cell A8 were both the "SysSampleCode" caption → same shared style. Mutating
  // a sub-property (a1.font = …) writes through that shared object, so A8's font changed too (user
  // saw A8 turn 18-bold). Replacing `.style` wholesale gives A1 its own object and leaves A8's
  // header style untouched. (fix 2026-09-11)
  a1.style = {
    font: { name: 'Calibri', size: 18, bold: true },
    alignment: { horizontal: 'center', vertical: 'middle' },
    fill: { type: 'pattern', pattern: 'none' },
    border: { top: thin, left: thin, right: thin, bottom: thin },
  } as import('exceljs').Style;
  r1.height = 33;
  if (lastVisible > 1) {
    // A merge INSIDE the caption row (PFAS 533's X1:Y1 — two duplicate captions, already cleared above) made
    // ExcelJS refuse the title merge ("Cannot merge already merged cells"), so the title sat un-centred in A1
    // (user 2026-10-01). Undo the row-1-only merges within the title's span first; a merge reaching below
    // row 1 is not part of the caption copy and is left alone (the title then stays in A1, as before).
    const colOf = (letters: string): number => { let n = 0; for (const ch of letters) n = n * 26 + (ch.charCodeAt(0) - 64); return n; };
    for (const m of [...((ws.model as { merges?: string[] }).merges ?? [])]) {
      const mm = /^([A-Z]+)(\d+):([A-Z]+)(\d+)$/.exec(m);
      if (!mm || mm[2] !== '1' || mm[4] !== '1') continue;
      if (colOf(mm[1]!) > lastVisible) continue;
      try { ws.unMergeCells(m); } catch { /* leave it — the title merge below then falls back to A1 */ }
    }
    try { ws.mergeCells(1, 1, 1, lastVisible); } catch { /* a merge reaching below row 1 — leave title in A1 */ }
  }
}

/** Expand ONE worksheet's mail-merge detail band against a record set — the generic
 *  form of OG's per-sheet DevExpress merge. Finds the detail band (the row with the
 *  most FIELD cells), duplicates it once per record, substitutes each, fills the header
 *  FIELD rows above it, and trims the template author-notes below it. `minBand` guards
 *  detection: the main sheet passes 5 (its header rows also hold a few FIELDs) so a
 *  sparse header row isn't mistaken for the band; a named sub-sheet passes 1 (its lone
 *  band row carries all its FIELDs). No band → substitute every FIELD once. */
function expandSheet(
  ws: import('exceljs').Worksheet,
  recs: Row[],
  headerUpper: Row,
  missing: Set<string>,
  fieldRefs: Record<string, string> | undefined,
  minBand: number,
  entrySet: Set<string> = new Set(),
  isEntryTime?: (key: string) => boolean,
  /** Normalised names of the fields whose HEADER cells take the batch header only (see continuousHeaderFields). */
  headerOnly: ReadonlySet<string> = new Set(),
): void {
  const aggCols = collectAggCols(ws);   // columns AVERAGEIFS/SUMIFS aggregate → their FIELD values must be numeric
  let band = -1, best = 0;
  for (let r = 1; r <= Math.min(ws.rowCount, 60); r++) {
    let n = 0;
    ws.getRow(r).eachCell({ includeEmpty: false }, c => {
      if (typeof c.formula === 'string' && /FIELD\(/i.test(c.formula)) n++;
    });
    if (n > best) { best = n; band = r; }
  }
  if (band > 0 && best >= minBand) {
    // G5's frozen duplicate of the detail-range header (worksheet row 1) → replaced with a
    // centered test-name TITLE banner (guarded by a column-for-column subset match against the
    // header row above the band; falls back to hiding when no test name is known). (2026-09-11;
    // removed 2026-09-26 to match OG, restored 2026-10-01 at the user's request.)
    titleizeDuplicateTopHeader(ws, band, String(headerUpper.TEST ?? '').trim());
    const n = Math.max(1, recs.length);
    if (n > 1) {
      // Capture the band row's merged-cell COLUMN SPANS first: ExcelJS duplicateRow copies
      // values + styles but NOT merges, so a wide single-field cell (e.g. SiteName merged C:E)
      // survives only on the FIRST data row and overflows/duplicates across C-E on the rest —
      // and SdmsUniverSheet's collapse pass needs the merge on EVERY data row to fire. Re-apply
      // each band-row merge to every duplicated row. (2026-09-09)
      const bandCols: Array<{ l: string; r: string }> = [];
      for (const m of ((ws.model as { merges?: string[] }).merges ?? [])) {
        const mm = /^([A-Z]+)(\d+):([A-Z]+)(\d+)$/.exec(m);
        if (mm && Number(mm[2]) === band && Number(mm[4]) === band) bandCols.push({ l: mm[1]!, r: mm[3]! });
      }
      ws.duplicateRow(band, n - 1, true);   // copy styles + formulas into the new rows
      for (let r = band; r <= band + n - 1; r++)
        for (const bc of bandCols) { try { ws.mergeCells(`${bc.l}${r}:${bc.r}${r}`); } catch { /* already merged / overlap */ } }
    }
    for (let i = 0; i < n; i++) {
      const rec = i < recs.length ? recs[i]! : {};
      substituteRow(ws.getRow(band + i), rec, headerUpper, i, missing, /* dropStaleCache */ true, fieldRefs, ws.name, aggCols, entrySet, isEntryTime);
    }
    // Header FIELD rows (above the band) — single substitution, no row shift. A header FIELD resolves
    // from the sheet's LAST data record, as DevExpress' mail merge leaves it (the header keeps the last
    // record's values): OG's Summary / Sequence "Analyzed Date" is the LAST run's ANALYZEDDATE on VOC 8086
    // and THM 8112 (4 of 5 batches; the 5th OG sheet holds stale caches) — G6 showed the batch creation
    // date with no time. A blank there falls back to the batch HEADER (a fresh batch has no analysed
    // dates yet), then to the header dataset. Per-record columns only in the record (TSS 1026's
    // UserDefinedDate3..18 in G2:N5 → start / end date-time T3:U6) still resolve. (2026-09-24, last-record
    // rule 2026-09-28)
    const lastRec = recs[recs.length - 1] ?? {};
    const hdrRec: Row = { ...headerUpper };
    const hdrKeyOf = new Map(Object.keys(headerUpper).map(hk => [normKey(hk), hk] as const));
    for (const [k, v] of Object.entries(lastRec)) {
      if (v === null || v === undefined || String(v).trim() === '') continue;
      if (headerOnly.has(normKey(k))) continue;
      const hk = hdrKeyOf.get(normKey(k));
      if (hk !== undefined) delete hdrRec[hk];
      hdrRec[k] = v;
    }
    // A Continuous field in a header cell (VOC Remark at B6) keeps the batch header's value, or none: OG's save
    // stored the band COLUMN's value for it (B8.. = the run's QC type), so the records' value is not what this
    // cell showed — the re-merge put "CCV" / "Sample" under Remarks where OG's sheet is blank (user 2026-09-30).
    // Present-but-null so the first-record fallback below cannot supply it either.
    for (const k of headerOnly) if (!hdrKeyOf.has(k)) hdrRec[k] = null;
    const firstRec = recs[0] ?? {};
    // dropStaleCache: a header formula's cached result is template-authoring junk too — TSS T3:U6 carry
    // "#VALUE!" (ExcelJS reads it back as NaN), which the converter seeded and the restore pass put back
    // over Univer's correct date-time. A fresh merge has no legitimate caches. (2026-09-24)
    for (let r = 1; r < band; r++) substituteRow(ws.getRow(r), hdrRec, firstRec, 0, missing, /* dropStaleCache */ true, fieldRefs, ws.name, aggCols, undefined, isEntryTime);
    // Trim template author-notes BELOW the band (OG's DevExpress merge emits only the
    // HEADERRANGE/DETAILRANGE). `row.values = []`, not spliceRows (a silent no-op at the
    // sheet tail). Edits the in-memory workbook only; the stored template blob is untouched.
    const lastDataRow = band + n - 1;
    for (let r = ws.rowCount; r > lastDataRow; r--) ws.getRow(r).values = [];
  } else {
    for (let r = 1; r <= ws.rowCount; r++) substituteRow(ws.getRow(r), {}, headerUpper, 0, missing, false, fieldRefs, ws.name, aggCols, undefined, isEntryTime);
  }
}

/** Column letters that a AVERAGEIFS/SUMIFS in THIS sheet aggregates (the first arg, e.g. the
 *  "G" in `AVERAGEIFS(G:G, A:A,"SAMPLE", …)`). The QC parent-lookup stats average the result
 *  column across rows, and AVERAGEIFS/SUMIFS silently IGNORE text cells — so a pure-FIELD result
 *  written as text makes DupSource/SpikeSource Result → Rec%/RPD%/QCStatus blank on reopen for
 *  the sample-derived rows. Detecting the aggregated COLUMN from the template's own formulas is
 *  what makes the numeric coercion global: 4014 binds it to NumericResult, 1016 to UserDefined14,
 *  others to a computed (already-numeric) column that needs nothing. */
function collectAggCols(ws: import('exceljs').Worksheet): Set<string> {
  const cols = new Set<string>();
  const re = /\b(?:AVERAGEIFS|SUMIFS)\s*\(\s*\$?([A-Z]{1,3})/gi;
  for (let r = 1; r <= Math.min(ws.rowCount, 60); r++) {
    ws.getRow(r).eachCell({ includeEmpty: false }, cell => {
      const f = typeof cell.formula === 'string' ? cell.formula : '';
      if (!f) return;
      let m; re.lastIndex = 0;
      while ((m = re.exec(f)) !== null) cols.add(m[1]!.toUpperCase());
    });
  }
  return cols;
}

/** Substitute the FIELD() cells in one row: pure FIELD → literal value; embedded
 *  FIELD (inside IF/IFERROR/…) → the value spliced into the formula, whose relative
 *  refs are shifted by `k` for the k-th repeated band row. */
function substituteRow(row: ReturnType<import('exceljs').Worksheet['getRow']>, rec: Row, headerUpper: Row, k: number, missing?: Set<string>, dropStaleCache = false, fieldRefs?: Record<string, string>, sheetName?: string, aggCols?: Set<string>, entrySet?: Set<string>, isEntryTime?: (key: string) => boolean): void {
  row.eachCell({ includeEmpty: false }, cell => {
    // Is this cell's column cross-row aggregated (AVERAGEIFS/SUMIFS)? → its FIELD value must be numeric.
    const colIsAgg = aggCols ? aggCols.has(/^([A-Z]+)/.exec(cell.address)?.[1] ?? '') : false;
    const f = cell.formula;
    if (typeof f === 'string') {
      // Formula cell — may embed FIELD("…") and/or [[…]] merge fields.
      if (HAS_FIELD.test(f) || HAS_BRACKET.test(f)) {
        const pure = f.match(PURE_RE);
        if (pure) {
          setFieldCell(cell, lookup(pure[1]!, rec, headerUpper, missing), pure[1]!, colIsAgg, !!entrySet?.has(normKey(pure[1]!)),
            isEntryTime ? isEntryTime(normKey(pure[1]!)) : true);
          // Remember WHERE this field landed so a value that only exists after the
          // merge can still be written into the right cell.
          if (fieldRefs && sheetName) {
            const key = pure[1]!.toUpperCase();
            if (!fieldRefs[key]) fieldRefs[key] = `${sheetName}!${cell.address}`;
            let bySheet = refsBySheet.get(fieldRefs);
            if (!bySheet) { bySheet = new Map(); refsBySheet.set(fieldRefs, bySheet); }
            let sheets = bySheet.get(key);
            if (!sheets) { sheets = new Map(); bySheet.set(key, sheets); }
            if (!sheets.has(sheetName)) sheets.set(sheetName, cell.address);
          }
        } else {
          const dateCell = isDateNumFmt(cell.numFmt);
          let sub = f.replace(FIELD_RE, (_m, name) => fieldLiteral(lookup(name, rec, headerUpper, missing), dateCell));
          sub = sub.replace(BRACKET_RE, (_m, name) => fieldLiteral(lookup(name, rec, headerUpper, missing), dateCell));
          cell.value = { formula: shiftFormula(sub, k) };
        }
      } else if (k || dropStaleCache) {
        // A non-FIELD formula copied into a repeated band row still needs its
        // same-row refs shifted (e.g. AT8 → AT9). Re-setting the cell also DROPS
        // the template's cached result — and `dropStaleCache` forces that on the
        // FIRST band row (k=0) too: DevExpress saves whatever the authoring
        // session last displayed, so the band carries junk caches like
        // '[NUMERICRESULT]', and since the Univer converter now SEEDS cached
        // results as display values (team 2026-08-16), row 10 of a FRESH merge
        // rendered that junk in the Result column while rows 11+ (re-set here)
        // were clean — user pH side-by-side 2026-08-17. A fresh merge has no
        // legitimate caches; saved sheets (real entered values) don't pass
        // through this merge, so their seeds are unaffected. (fix 2026-08-17)
        cell.value = { formula: shiftFormula(f, k) };
      }
      return;
    }
    // Plain TEXT cell carrying a `[[COLUMN]]` placeholder (the CtrlLimit columns):
    // whole-cell → the raw value; embedded → substitute each placeholder inline.
    const v = cell.value;
    if (typeof v === 'string' && HAS_BRACKET.test(v)) {
      const pure = v.match(BRACKET_PURE_RE);
      if (pure) {
        setFieldCell(cell, lookup(pure[1]!, rec, headerUpper, missing), pure[1]!, colIsAgg, !!entrySet?.has(normKey(pure[1]!)),
            isEntryTime ? isEntryTime(normKey(pure[1]!)) : true);
      } else {
        cell.value = v.replace(BRACKET_RE, (_m, name) => {
          const val = lookup(name, rec, headerUpper, missing);
          return val === undefined || val === null ? '' : String(val);
        });
      }
    }
  });
}
